import React, { Fragment, useEffect } from 'react';
import { styled, Button, Checkbox, Box, Text, Flex } from '@goodrx/matisse';
import { PageHeading } from 'app/components/PageHeading';
import { Divider } from 'app/components/core';
import * as Analytics from 'app/lib/analytics';
import { CtaContainer } from 'app/components/auth/SignUp/CtaContainer';
import { SignInCta } from 'app/components/auth/SignUp/SignInCta';
import { AuthLayout } from 'app/components/auth/AuthLayout';
import { CdnImage } from 'app/components/CdnImage';
import { serviceDrugsOptions } from 'app/components/auth/SignUp/SignUpWithEligibility/serviceDrugsOptions';
import { sessionStorage } from 'app/lib/storage';
import { REQUESTED_DRUG_KEY } from 'app/stores/services';

const StyledPageHeading = styled(PageHeading)`
  p {
    font-weight: 500;
  }
`;

const StyledDivider = styled(Divider)<{ marginBottom: number; marginTop: number }>`
  margin-top: 24px;
  margin-bottom: ${(p) => (Number.isInteger(p.marginBottom) ? `${p.marginBottom}px` : '24px')};
  margin-top: ${(p) => (Number.isInteger(p.marginTop) ? `${p.marginTop}px` : '24px')};
`;

interface DrugSelectionProps {
  nextStep: () => void;
  selectedDrug?: string;
  serviceCode: 'ED' | 'BIRTH_CONTROL';
  setSelectedDrug: (drug: string) => void;
}

export const DrugSelection = ({ nextStep, selectedDrug, setSelectedDrug, serviceCode }: DrugSelectionProps) => {
  function handleContinue() {
    Analytics.trackEvent('Drug Selection CTA Clicked', {
      selectedDrug,
      buttonName: 'Continue',
      page: 'Drug Selection',
    });

    nextStep();
  }

  //preselect first one by default
  useEffect(() => onChange(serviceDrugsOptions[serviceCode][0].title), [serviceCode, setSelectedDrug]);

  const onChange = (drug: string) => {
    sessionStorage.set(REQUESTED_DRUG_KEY, { display_name: drug, service: serviceCode });

    Analytics.trackEvent('Drug Selection Option Selected', {
      optionName: drug,
      page: 'Drug Selection',
    });

    setSelectedDrug(drug);
  };

  return (
    <>
      <CtaContainer>
        <SignInCta small />
      </CtaContainer>
      <AuthLayout>
        <StyledPageHeading
          title="You’re Eligible!"
          subtitle={
            serviceCode === 'ED'
              ? 'Select your preferred ED medication below. Not sure? We can help recommend the best options for you.'
              : 'Select your preferred birth control method below. Not sure? We can help recommend the best options for you.'
          }
          color="greyDarkest"
          mb={0}
        />
        <StyledDivider />
        {serviceDrugsOptions[serviceCode].map((option) => (
          <Fragment key={option.title}>
            <Checkbox
              isRadio
              name={option.title}
              value={selectedDrug}
              valueTrue={option.title}
              valueFalse={option.title}
              onChange={onChange}
              label={
                <Flex>
                  <Box>
                    <Text as="h3" m={0} fontSize={16}>
                      {option.title}
                    </Text>

                    <Text as="p" color="greyDarkest" mt="8px" mb={0} fontSize={14} fontWeight={500}>
                      {option.subtitle}
                    </Text>
                  </Box>
                  {option.icon && (
                    <Box ml={3}>
                      <CdnImage src={option.icon} alt={option.title} width={40} height={40} />
                    </Box>
                  )}
                </Flex>
              }
            />

            <StyledDivider marginBottom={20} marginTop={20} />
          </Fragment>
        ))}
        <Button variant="primary" size="lg" block mt={1} onClick={handleContinue} disabled={!selectedDrug}>
          Continue
        </Button>
      </AuthLayout>
    </>
  );
};
